import React, { useState } from 'react';
import { UseMutateFunction } from '@tanstack/react-query';

import { getFileExtension } from '@hooks/dataSources/useMutateMavenirConfigUpload';
import { useMutation } from '@hooks/reactQuery';

import { useAppDispatch } from '@store/store';

import { setSnackbar } from '@slices/common';

import { uploadFileToGcloud } from '@api/common';
import { createUploadUrlGoldenSettings } from '@api/goldenSettings';

import { IUploadUrlType } from '@interfaces/import/import';

import { ESnackbarTypes } from '@enums/common';

import { HUNDRED_PERCENT_LOADING } from '@constants/common';
import { UPLOAD_URL_ATTACHMENT } from '@constants/queryKeys';
import { responseMessages } from '@constants/responseMessages';

interface IUseMutateCreateUploadUrlGoldenSettingsReturnType {
	createUploadUrlAttachment: UseMutateFunction<any, any, IUploadUrlType, any>;
	isLoadingCreateUploadUrlAttachment: boolean;
	progress: number;
}

export const useMutateCreateUploadUrlGoldenSettings = (
	setImportedFile: React.Dispatch<React.SetStateAction<{ importId: string; name: string; file: File } | undefined>>,
): IUseMutateCreateUploadUrlGoldenSettingsReturnType => {
	const [progress, setProgress] = useState<number>(HUNDRED_PERCENT_LOADING);

	const dispatch = useAppDispatch();

	const {
		mutate: createUploadUrlAttachment,
		isLoading,
	} = useMutation<IUploadUrlType>({
		mutationKey: [UPLOAD_URL_ATTACHMENT],
		mutationFn: async ({ files }: IUploadUrlType) => {
			for (const file of files) {
				const fileType = getFileExtension(file);

				if (!fileType.length) {
					return;
				}

				const data = await createUploadUrlGoldenSettings({ fileName: file.name });
				const url = data?.uploadUrl;

				await uploadFileToGcloud({ url, data: file, onProgressHandler: setProgress });

				setImportedFile({
					importId: data?.importId,
					name: file.name,
					file,
				});

			}
		},
		onError: (err: Error) => {
			if (err instanceof Error) {
				dispatch(setSnackbar({
					message: err?.message || responseMessages.error,
					type: ESnackbarTypes.error,
				}));
			}
		},
	});

	return {
		createUploadUrlAttachment,
		isLoadingCreateUploadUrlAttachment: isLoading,
		progress,
	};
};
